/**
 * Materials. Two halves, and the first one is the one that matters now.
 *
 * ## 1. The stylised path — `stylisedMaterial` / `setStylisedSun`
 *
 * Everything that scales with the crowd (characters, gear, props) is a `MeshBasicNodeMaterial`
 * whose lighting is a half-Lambert term against one module-level sun uniform, plus an optional
 * Fresnel rim. It is not a `MeshStandardNodeMaterial` and that is the entire point:
 *
 * - **No lighting system.** `MeshBasicNodeMaterial` never builds a lights node, so the shader
 *   does not depend on how many lights are in the scene and cannot be re-keyed by one going on
 *   or off. Three hashes the *visible* light set into every material's cache key, which is how
 *   a muzzle flash used to rebuild every material in the scene (PERF-BUDGET rule 3). A material
 *   with no lights node is immune by construction.
 * - **No IBL.** No PMREM sample, no environment node, no second cache-key axis.
 * - **One uniform write moves the sun.** `setStylisedSun` writes three module-level uniforms
 *   and rebuilds nothing, whatever the map is holding.
 * - **It composes with a caller's own vertex work.** The lighting is built from TSL's
 *   `normalWorld`, which resolves through the material's `normalNode` before transforming to
 *   world space — so `render/crowd.ts` can skin in the vertex stage and hand this material its
 *   own normal. (That normal must be **view-space**: `normalWorld` is
 *   `setupNormal().transformNormalByInverseViewMatrix(cameraViewMatrix)`.)
 *
 * ## 2. Surface-material upgrades: what makes a greybox read as *matter* under the Still
 *
 * The Still sits at 4.5° above the horizon and never moves, so every surface in STILLSUN is
 * lit at a grazing angle forever. That is a brutal lighting rig for materials: N·L is
 * roughly sin(elevation + local slope), so a couple of degrees of normal perturbation is the
 * difference between a lit pixel and a black one, and any albedo variation is multiplied by a
 * lighting term that is already swinging hard. It is also a *generous* rig, because grazing
 * light is exactly what reveals surface: worn edges catch it, crevices stay in shadow, dust
 * streaks run visibly down a wall, and sand throws back sparkle and an opposition surge.
 *
 * This module adds those cues on top of the albedo textures `scene.ts` and `terrain.ts`
 * already load. Nothing here downloads anything — it is all noise, hashes and a luminance
 * read of albedo the scene already has on the GPU.
 *
 * ## Rules the whole file obeys
 *
 * - **No per-frame graph rebuilds.** Every node below is built exactly once, when the
 *   material is upgraded. Anything dynamic (the sun direction, the lantern flicker) goes
 *   through a `uniform()` or through `time`, which change a buffer's contents and *not* the
 *   node graph — a rebuilt graph changes the material's cache key and recompiles the shader,
 *   which on this project's GPU costs seconds of frozen frame (WEBGPU-NOTES §1, §3).
 * - **Cheap.** A few noise/hash evaluations per fragment, no loops. Everything fine enough
 *   to alias is weighted by a view-distance term and gone within tens of metres, the same
 *   "distance budget" idea `terrain.ts` uses — an effect that keeps full contrast to the fog
 *   line does not mip, does not resolve, and reads as television static.
 * - **Fail soft.** Each entry point wraps its whole graph build in a try/catch and leaves the
 *   material exactly as it found it on failure. A material that loses its wear detail is a
 *   material that still renders; a thrown TSL error is a black screen.
 * - **`?noise=0` still works.** The upgrades honour `renderFlags.noise` so the existing
 *   perf-bisect kill switch keeps bisecting.
 * - **Call each upgrade once per material.** They are *not* idempotent by design: an upgrade
 *   reads the material's existing nodes as its base, so a second call would read its own
 *   output and stack wear on wear (and would replace `userData.sunDirUniform` while the
 *   original uniform was still wired into the graph). A `setMap` path that rebuilds materials
 *   is fine; one that re-upgrades a surviving material is not.
 *
 * @see docs/ART-DIRECTION.md — palette and "golden-hour photograph of a dead ship" brief.
 */

import {
  Color,
  DoubleSide,
  MeshBasicNodeMaterial,
  MeshStandardNodeMaterial,
  Vector3,
  type Texture,
} from 'three/webgpu';
import * as TSL from 'three/tsl';
import { renderFlags } from './renderer.js';

/** Loosely-typed handle on a TSL node: every operator returns another node. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyNode = any;
type NodeFn = (...args: AnyNode[]) => AnyNode;

/**
 * three's TSL typings are far stricter than the JavaScript they describe, and this file
 * composes nodes read back off a material (`mat.colorNode` is typed `Node | null`, with no
 * static knowledge that it is a vec3) with nodes it builds itself. Rather than sprinkle casts
 * through every expression, the graph goes through these loose aliases.
 */
const {
  color,
  dot,
  float,
  floor,
  fract,
  max,
  mix,
  mx_noise_float,
  pow,
  reflect,
  sin,
  smoothstep,
  step,
  texture,
  uniform,
  vec2,
  vec3,
  vec4,
} = TSL as unknown as Record<
  | 'color'
  | 'dot'
  | 'float'
  | 'floor'
  | 'fract'
  | 'max'
  | 'mix'
  | 'mx_noise_float'
  | 'pow'
  | 'reflect'
  | 'sin'
  | 'smoothstep'
  | 'step'
  | 'texture'
  | 'uniform'
  | 'vec2'
  | 'vec3'
  | 'vec4',
  NodeFn
>;
const cameraPosition: AnyNode = TSL.cameraPosition;
const normalView: AnyNode = TSL.normalView;
const normalWorld: AnyNode = TSL.normalWorld;
const positionView: AnyNode = TSL.positionView;
const positionViewDirection: AnyNode = TSL.positionViewDirection;
const positionWorld: AnyNode = TSL.positionWorld;
const time: AnyNode = TSL.time;

// =============================================================================================
// The stylised path
// =============================================================================================

/**
 * The sun, as three module-level uniforms shared by every stylised material ever built. This
 * is what makes `setStylisedSun` O(1): it writes three buffers, not N materials, and no node
 * graph is touched — so a day cycle, a cutscene light change or a zone transition costs
 * nothing and recompiles nothing.
 *
 * Defaults are **Bonelight at noon** — the approved colour story for the Vigil Downs — so a prop
 * built before the scene has told us where the sun is reads as part of this world rather than as
 * the old amber-on-charcoal it was seeded with. They are the day stop of `render/sky.ts`'s ramp:
 * key `#FFF6E2`, and an ambient that is the same half-and-half mix of the hemisphere's sky
 * (`#F6F0E2`) and ground (`#D3CDB8`) that `scene.ts` hands `setStylisedSun` every frame. Getting
 * that mix right matters more than it looks: it is the colour of every surface facing away from
 * the key, which in a bone world is most of what the eye reads as material.
 */
const uStylisedSunDir: AnyNode = uniform(new Vector3(0.34, 0.72, 0.6).normalize());
const uStylisedSunColour: AnyNode = uniform(new Color(0xfff6e2));
const uStylisedAmbient: AnyNode = uniform(new Color(0xe5dfcd));

/**
 * Exponent on the half-Lambert term. 1 is the flat, everything-is-lit Valve original; 2 is
 * the usual squared form and is what is used here — it puts a readable terminator back on a
 * character's cheek and shoulder without ever reaching black, which is the whole reason to
 * wrap the diffuse in the first place. A gradient atlas ramps this into bands.
 *
 * **Do not tune this in isolation.** `render/crowd.ts`'s skinned shader hard-codes the same
 * wrap as `lambert.mul(lambert)`; the two have to agree exactly or a prop and a person standing
 * next to each other under one sun are lit differently. The Bonelight recolour deliberately
 * moved only the three uniforms above and left the exponent where crowd.ts can match it.
 */
const HALF_LAMBERT_POWER = 2;
/** Fresnel rim: exponent and strength, when `opts.rim` asks for one. */
const RIM_POWER = 3;
const RIM_STRENGTH = 0.35;
/**
 * How far the rim leans from the ambient toward the key. The rim used to be the raw key colour,
 * which under Bonelight put a `#FFF6E2` edge on a `#FBF7EC` prop — an invisible highlight by day
 * and a hot white outline at midnight, when the key is still bone but everything around it has
 * gone to `#241C33`. Half-way between the two tracks the ramp instead: pale and barely-there at
 * noon, a cool moon-silver edge after dark, and it costs no second uniform and no second cache-key
 * axis, so it is still one lighting term (LOW-POLY-BIBLE: one sun, one hemisphere, nothing else).
 */
const RIM_KEY_LEAN = 0.5;

export interface StylisedOptions {
  /** Add a grazing-angle rim in the sun's colour. Off by default — it is a highlight for hero
   *  silhouettes (characters, signature props), not a global. */
  rim?: boolean;
  /** Render both faces. For cards and cut-out geometry: cloaks, foliage, banners. */
  doubleSided?: boolean;
}

/**
 * The one character/prop material.
 *
 * `colorNode` is: gradient-atlas albedo (or white when `map` is null) times
 * `ambient + sunColour * halfLambert^2`, plus an optional Fresnel rim. Alpha passes straight
 * through from the texture, so `alphaTest` cut-outs work without any extra plumbing.
 *
 * The lighting is built from `normalWorld`, never from a geometry attribute. `normalWorld`
 * resolves through `NodeMaterial.setupNormal()` — `this.normalNode ? vec3(this.normalNode) :
 * materialNormal` — and only then transforms by the inverse view matrix, so a caller that
 * overrides `positionNode` and `normalNode` (the crowd renderer skins in the vertex stage and
 * supplies its own) gets lit by *its* normal rather than by the undeformed one. The contract
 * that comes with that: **a caller's `normalNode` must be in view space**, like every other
 * `normalNode` three consumes. `normalWorldGeometry` is deliberately not used here — it is
 * built from the geometry attribute and would ignore a skinned normal entirely.
 *
 * @param map  Gradient/colour atlas, or null for a flat white base to be tinted by
 *             `instanceColor`/vertex colours.
 */
export function stylisedMaterial(
  map: Texture | null,
  opts?: StylisedOptions,
): MeshBasicNodeMaterial {
  const mat = new MeshBasicNodeMaterial();
  if (opts?.doubleSided === true) mat.side = DoubleSide;
  // Deliberately not `mat.map = map`: `colorNode` wins in `setupDiffuseColor`, so assigning
  // the map as well would only bind a second, unread sampler.
  try {
    const albedo: AnyNode = map !== null ? texture(map) : vec4(1, 1, 1, 1);

    // Half-Lambert. `dot` on a world-space normal against a world-space sun uniform; remapped
    // to 0..1 so the unlit side reads as its ambient colour rather than black, then raised to
    // put the terminator back.
    //
    // `mix(ambient, sun, t)` and not `ambient + sun * t` — deliberately the same expression
    // `render/crowd.ts`'s skinned shader uses, so a prop and a character standing next to each
    // other under the same sun are lit identically. An additive form would have made every
    // prop a shade brighter than every body and nobody would have known why.
    const ndl = dot(normalWorld, uStylisedSunDir);
    const halfLambert = pow(ndl.mul(0.5).add(0.5), HALF_LAMBERT_POWER);
    const light: AnyNode = mix(uStylisedAmbient, uStylisedSunColour, halfLambert);

    let rgb: AnyNode = albedo.rgb.mul(light);

    if (opts?.rim === true) {
      // View-space Fresnel: `positionViewDirection` is the normalised fragment-to-eye vector
      // and pairs with `normalView`, which resolves through the caller's `normalNode` exactly
      // as `normalWorld` does one line above.
      const facing = max(dot(normalView, positionViewDirection), 0);
      const rimTint = mix(uStylisedAmbient, uStylisedSunColour, RIM_KEY_LEAN);
      rgb = rgb.add(rimTint.mul(pow(facing.oneMinus(), RIM_POWER)).mul(RIM_STRENGTH));
    }

    mat.colorNode = vec4(rgb, albedo.a);
  } catch (err) {
    // Same contract as the upgraders below: a material that loses its shading still renders.
    console.warn('[stillsun] stylised material graph unavailable', err);
  }
  return mat;
}

/**
 * Move the sun for every stylised material at once. **A uniform write only** — three buffers,
 * no graph rebuild, no material walk, no shader recompile, whatever is on screen.
 *
 * `render/crowd.ts` keeps its own identical trio behind `setCrowdSun(dir, sunColour, ambient)`
 * (its shader is generated inside the skinning `Fn`, so it cannot share these). Call both from
 * the same place with the same arguments, or the props and the people will disagree about
 * where the sun is.
 *
 * There is no intensity argument, and that is a trap worth naming: this path multiplies nothing,
 * so `sunColour` has to arrive **already scaled by the key's strength**. Handing it the sky's raw
 * `sunColor` lights every prop with a full-power bone-white moon at midnight while the lit world
 * around it correctly goes dark. `render/sky.ts` exposes `keyColorScaled` (= `sunColor *
 * keyIntensity`) for exactly this call and for `setCrowdSun`.
 *
 * @param dir       Direction **toward** the sun, world space. Normalised here.
 * @param sunColour Key colour *times key intensity*, in the renderer's working space (a
 *                  `new Color(hex)` is already converted). Values above 1 are allowed and are
 *                  what the bloom picks up.
 * @param ambient   Fill colour applied everywhere, including the unlit side.
 */
export function setStylisedSun(dir: Vector3, sunColour: Color, ambient: Color): void {
  (uStylisedSunDir.value as Vector3).copy(dir).normalize();
  (uStylisedSunColour.value as Color).copy(sunColour);
  (uStylisedAmbient.value as Color).copy(ambient);
}

// =============================================================================================
// Surface upgrades (the lit world)
// =============================================================================================

/**
 * Quality tier for material detail. Structurally identical to `renderer.ts`'s `QualityTier`,
 * but declared locally so this module has no import-time dependency on the renderer's tier
 * enum — a caller holding a `QualityTier` passes it straight through either way.
 *
 * `'ultra'` deliberately behaves exactly like `'pc'` here. Every term in this file is either
 * cheap enough for any desktop GPU or already at the aliasing limit for its frequency, so an
 * ultra-only extra octave would cost frames and buy shimmer. Per renderer.ts's own guidance,
 * the tier tests below are all `tier !== 'low'` rather than `tier === 'pc'`, so nothing
 * silently drops out of ultra.
 */
export type MaterialTier = 'low' | 'pc' | 'ultra';

/** Rec. 709 luma weights — how bright a texel *reads*, which is the cue we treat as relief. */
const LUMA = [0.2126, 0.7152, 0.0722] as const;

/** Perceived brightness of an albedo node, as a float node in roughly 0..1. */
function luma(rgb: AnyNode): AnyNode {
  return dot(rgb, vec3(LUMA[0], LUMA[1], LUMA[2]));
}

/**
 * Classic sin-hash, 2D -> [0,1). Deterministic per input, no texture, one `sin` and one
 * `fract`.
 *
 * The input is wrapped into 0..`HASH_DOMAIN` first, and that wrap is the entire reason this
 * helper exists instead of the expression being inlined. The naive Shadertoy form is fed
 * world-space coordinates here, and a raid map is ~260 m across: at 48 cells/m that is a cell
 * index near 12 500, whose dot product lands around 5.5e6. float32 has ~7 significant digits,
 * so `sin` is being asked for the sine of a number whose own representation error is a
 * meaningful fraction of a radian — and drivers vary wildly in how (or whether) they
 * range-reduce that far out. The result is a hash that degenerates into banding, or into a
 * constant, precisely at the edges of the map. Wrapping the domain first costs one divide,
 * one `fract` and one multiply per fragment and keeps the sparkle field statistically
 * identical everywhere.
 *
 * `fract` (rather than a `%`/`mod`) because the wrap must behave on negative coordinates, and
 * half of every map is at negative x or z.
 *
 * The domain is the trade: too large and the precision problem comes back, too small and the
 * hash *repeats*. At 1024 the sparkle field's period is 1024/48 ~= 21 m — far enough apart
 * that no two visible constellations of glints are the same one — while the largest dot
 * product stays near 4.5e5, where a float32 ulp is ~0.03 rad against a per-cell argument step
 * of 127.1. Three orders of margin, which is the whole point of wrapping.
 */
const HASH_DOMAIN = 1024;
function hash2(p: AnyNode): AnyNode {
  const wrapped = fract(p.div(HASH_DOMAIN)).mul(HASH_DOMAIN);
  return fract(sin(dot(wrapped, vec2(127.1, 311.7))).mul(43758.5453));
}

/**
 * Mikkelsen's surface-gradient normal perturbation, the same inline port `terrain.ts` uses
 * for its wind-ripple normal.
 *
 * The built-in `bumpMap()` node reads its height by offsetting a UV, which is a no-op for a
 * height authored directly in world space (ours are: albedo luminance, world-space noise).
 * So the screen-space derivatives of the height itself are fed in instead, against the
 * screen-space derivatives of `positionView`. Kept as a local function taking `AnyNode`
 * because TSL's node types have no clean standalone spelling to annotate a parameter with —
 * hence the loose alias at the top of the file.
 *
 * @param base   The normal to perturb, in **view space** (`normalView`, or a material's
 *               existing `normalNode` if it already perturbed it once).
 * @param height A world-space height field, in metres, as a float node.
 */
function perturbNormal(base: AnyNode, height: AnyNode): AnyNode {
  const dpdx = positionView.dFdx();
  const dpdy = positionView.dFdy();
  const r1 = dpdy.cross(base);
  const r2 = base.cross(dpdx);
  const det = dpdx.dot(r1);
  const grad = det.sign().mul(height.dFdx().mul(r1).add(height.dFdy().mul(r2)));
  return det.abs().mul(base).sub(grad).normalize();
}

// =============================================================================================
// Hull plating
// =============================================================================================

/**
 * The luminance window the plating's relief is remapped across — **measured, not guessed**,
 * and the single most load-bearing pair of numbers in this file: every wear and grime mask
 * hangs off the `relief` term they define.
 *
 * These are *linear* values. `loadTiledTexture` uploads the plating with `SRGBColorSpace`, so
 * the sampler hands the shader linear radiance, not the 0..1 sRGB the PNG looks like in an
 * image viewer. Measured over all 1024x1024 texels of `hull-plating-scorched.png`: mean 0.056,
 * p20 0.0345, p50 0.0487, p80 0.0769, p99 0.140 linear (0.20 / 0.24 / 0.31 / 0.41 in sRGB —
 * which is where a naive 0.1..0.4 window comes from, and why one would have pinned `relief` to
 * ~0 over the entire wall: no wear anywhere, grime everywhere, and no error to show for it).
 *
 * The p20/p80 window also happens to bracket the flat fallback colour `0x4e5257`, whose linear
 * luminance is ~0.030 — so a raid with `?tex=0` still gets a live (if uniform) relief term
 * rather than a degenerate one.
 */
const HULL_RELIEF_LO = 0.034;
const HULL_RELIEF_HI = 0.077;
/** Bare metal under the scorch, where an edge has been rubbed back to the plate. */
const HULL_BARE_METAL = 0x9a9ca0;
/** Soot and settled dust packed into seams and rivet wells. */
const HULL_GRIME = 0x1b1714;
/** Oxide/dust run — the colour of a streak weeping down from a fastener. */
const HULL_STREAK = 0x5a3a28;
/** Roughness of rubbed-back plate: polished by contact, not by a finisher. */
const HULL_WEAR_ROUGHNESS = 0.3;
/** Metalness of rubbed-back plate: paint and scorch gone, so nearly pure conductor. */
const HULL_WEAR_METALNESS = 0.9;
/** Roughness of packed grime: fully diffuse, kills the plate's specular in the seam. */
const HULL_GRIME_ROUGHNESS = 0.85;
/** How far the grime lerp is allowed to go, so a seam darkens rather than going matte black. */
const HULL_GRIME_STRENGTH = 0.7;
/** Frequency of the mid-scale wear noise, in cycles per metre: ~1.25 m blotches. */
const HULL_WEAR_NOISE_SCALE = 0.8;
/** Frequency of the grime patchiness noise: ~3 m, so crevice dirt comes and goes by panel run. */
const HULL_GRIME_NOISE_SCALE = 0.35;
/** Peak roughness swing from a vertical streak, either side of the base. */
const HULL_STREAK_ROUGHNESS = 0.12;
/** Peak albedo tint toward `HULL_STREAK` inside a streak. */
const HULL_STREAK_TINT = 0.22;
/** Micro-relief amplitude in metres. Rivet heads and plate edges, not sheet deformation. */
const HULL_MICRO_BUMP_M = 0.02;
/** View distance (m) over which the micro-normal fades out. Past this the plate is smooth. */
const HULL_MICRO_NEAR = 18;
const HULL_MICRO_FAR = 25;

/**
 * Turns `scene.ts`'s flat brushed-steel hull material into worn plating.
 *
 * Call this **after** `scene.ts` has built the material (colour `0x4e5257`, metalness 0.6,
 * roughness 0.55, and — when `renderFlags.textures` allowed it — a `colorNode` holding a
 * triplanar world-space sample of the scorched plating PNG tiled every 6 m). The upgrade
 * reads whatever albedo is there and layers four cues on it:
 *
 * 1. **Edge wear.** Where the plating reads bright (a raised plate face, not a seam) *and* a
 *    mid-frequency world noise agrees, the albedo lifts toward bare metal, roughness drops
 *    and metalness rises. Both conditions have to hold, which is what keeps wear on the
 *    proud parts of the surface instead of dusting the whole wall evenly.
 * 2. **Grime.** Where the plating reads dark — a seam, a rivet well — soot packs in: darker,
 *    rougher, and weighted by a second noise so it is patchy rather than a uniform dirt pass.
 * 3. **Vertical streaking.** A noise stretched hard along world Y, so it forms runs rather
 *    than blobs, modulating roughness and tinting toward oxide. Walls only: a streak needs
 *    gravity to have made it, and a floor with vertical runs on it reads as a mistake.
 * 4. **Micro-relief.** The albedo's own luminance driven through the surface-gradient normal
 *    trick, so rivets and plate lips actually catch the Still instead of being painted on.
 *    Small amplitude and faded out by 25 m, because at 4.5° elevation a bump normal is a
 *    lighting switch, not a shading nudge.
 *
 * Everything is derived from the albedo and from world position, so one shared material still
 * covers every box in the map with no per-mesh state.
 *
 * @param mat  The hull material, mutated in place.
 * @param opts.tier Low skips the micro-normal entirely (two screen-space derivative pairs
 *                  over every hull pixel in the yard); the albedo/roughness/metalness layers
 *                  are a handful of ALU and stay on.
 */
export function upgradeHullMaterial(
  mat: MeshStandardNodeMaterial,
  opts: { tier: MaterialTier },
): void {
  // `?noise=0` is the perf-bisect kill switch the rest of render/ honours: it must leave the
  // material exactly as scene.ts built it, flat colour or plain texture and nothing else.
  if (!renderFlags.noise) return;
  try {
    // Whatever scene.ts managed to install. If the plating PNG failed to load (or textures
    // are off) this is the flat AD hull colour, and every luminance-driven term below simply
    // becomes constant — the wear/grime masks flatten out and the micro-normal's derivatives
    // go to zero. Degrading to "no visible change" is the correct failure here.
    const base: AnyNode = (mat.colorNode as AnyNode) ?? color(0x4e5257);
    const baseRoughness = float(mat.roughness);
    const baseMetalness = float(mat.metalness);

    // Luminance as a stand-in for relief. Scorched plating photographs with its raised plate
    // faces catching light and its seams/rivet wells in shadow, so the PNG's own brightness
    // already encodes the geometry we want to react to — no second texture, no height map.
    const lum = luma(base);
    // Remapped across the range the plating actually occupies (see HULL_RELIEF_LO/HI). Getting
    // this window wrong is silent: the material still renders, it just renders with one of the
    // two masks below pinned on and the other pinned off.
    const relief = smoothstep(HULL_RELIEF_LO, HULL_RELIEF_HI, lum);

    // --- 1. edge wear ------------------------------------------------------------------
    // Two independent gates multiplied together. Each passes roughly half its input, so the
    // product covers roughly a quarter of the surface — the ~25% wear budget. Coverage, not
    // blend weight: inside that quarter the mask is allowed to reach 1 and show real bare
    // metal, because a wear layer capped at 0.25 strength is a wear layer nobody can see.
    const wearNoise = mx_noise_float(positionWorld.mul(HULL_WEAR_NOISE_SCALE)).mul(0.5).add(0.5);
    const wear = smoothstep(0.45, 0.8, relief).mul(smoothstep(0.45, 0.75, wearNoise));

    let albedo: AnyNode = mix(base, color(HULL_BARE_METAL), wear);
    let roughness: AnyNode = mix(baseRoughness, float(HULL_WEAR_ROUGHNESS), wear);
    const metalness: AnyNode = mix(baseMetalness, float(HULL_WEAR_METALNESS), wear);

    // --- 2. grime in crevices ----------------------------------------------------------
    // The inverse gate: low relief means seam or rivet well, which is where soot and dust
    // collect. The second noise is what stops this reading as a uniform "dirty" pass — real
    // grime arrives by run-off and by traffic, so it comes and goes across a bulkhead.
    //
    // Written as an ascending `smoothstep` plus `oneMinus` rather than the descending
    // `smoothstep(0.5, 0.1, relief)` it replaces: both GLSL ES and WGSL declare the result
    // *undefined* when edge0 >= edge1. Today's backends evaluate the polynomial literally and
    // it happens to invert correctly, but this project ships a WebGL2 fallback path too, and
    // the ascending form is the house idiom (see post.ts's shadow/highlight weights).
    const grimeNoise = mx_noise_float(positionWorld.mul(HULL_GRIME_NOISE_SCALE)).mul(0.5).add(0.5);
    const grime = smoothstep(0.1, 0.5, relief)
      .oneMinus()
      .mul(smoothstep(0.35, 0.7, grimeNoise));
    // A lerp toward `HULL_GRIME`, not a multiply — but the effect is the multiply the brief
    // asks for, because that colour is darker than any pixel of the plating albedo, so this
    // blend can only ever darken. Capped short of 1 so a seam still shows its own texture.
    albedo = mix(albedo, color(HULL_GRIME), grime.mul(HULL_GRIME_STRENGTH));
    roughness = mix(roughness, float(HULL_GRIME_ROUGHNESS), grime);

    // --- 3. vertical streaking ---------------------------------------------------------
    // One noise lookup, sampled with world Y compressed 8:1 against world XZ, which stretches
    // its features into vertical runs — the shape of dust and oxide weeping down from a
    // fastener over a long time. `vec3(vec2, float)` is a supported TSL constructor (three's
    // own `NormalMapNode` builds `vec3( normalMap.xy.mul( scale ), normalMap.z )` the same way).
    const streakNoise = mx_noise_float(vec3(positionWorld.xz.mul(2), positionWorld.y.mul(0.25)));
    // Walls only. |normalWorld.y| < 0.5 is the wall half of the hemisphere; the fade band
    // means a chamfer or a leaning plate loses the streaks gradually instead of at a hard
    // silhouette line. Floors get none: run-off needs a vertical face to run down.
    const wallness = smoothstep(0.35, 0.5, normalWorld.y.abs()).oneMinus();
    // Signed, so roughness goes both ways around the base value: a streak is a wet-looking
    // dark run with a dried, dustier margin, and only modulating one direction reads as a
    // decal laid over the plate rather than as something that happened to it.
    const streak = streakNoise.mul(wallness);
    roughness = roughness.add(streak.mul(HULL_STREAK_ROUGHNESS));
    albedo = mix(
      albedo,
      color(HULL_STREAK),
      smoothstep(0.15, 0.6, streakNoise.mul(0.5).add(0.5)).mul(HULL_STREAK_TINT).mul(wallness),
    );

    // --- 4. micro-relief ---------------------------------------------------------------
    let normal: AnyNode = null;
    if (opts.tier !== 'low') {
      // A *linear* remap of the raw luminance, not `relief`: the smoothstep above clips the
      // top and bottom of the range flat, and a height field with flat regions has zero
      // gradient there — the rivets at the bright end and the seam floors at the dark end,
      // exactly the features this term exists to catch, would be the ones it missed. The
      // division is also what makes the amplitude constant mean what it says: the plating's
      // linear luminance only spans about 0.05, so `lum.mul(0.02)` unnormalised would be a
      // 1 mm bump, not the 2 cm one intended.
      const height = lum
        .sub(HULL_RELIEF_LO)
        .div(HULL_RELIEF_HI - HULL_RELIEF_LO)
        .mul(HULL_MICRO_BUMP_M);
      const perturbed = perturbNormal(normalView, height);
      // Faded on the finished normal rather than on the height, exactly as terrain.ts does.
      // Scaling the height by a distance term would put that term inside the dFdx/dFdy above,
      // and at a grazing angle depth changes fast enough per pixel that the fade's own screen
      // derivative would show up as a visible band across the wall. Interpolating between two
      // finished normals cannot.
      const near = smoothstep(HULL_MICRO_NEAR, HULL_MICRO_FAR, positionView.length()).oneMinus();
      normal = mix(normalView, perturbed, near).normalize();
    }

    // Every assignment happens here, after the last thing that can throw. Anything earlier
    // would leave a half-upgraded material on failure — new albedo and roughness against the
    // old normal — which is a worse outcome than no upgrade at all and much harder to spot.
    mat.colorNode = albedo;
    mat.roughnessNode = roughness.clamp(0.05, 1);
    mat.metalnessNode = metalness.clamp(0, 1);
    if (normal !== null) mat.normalNode = normal;
  } catch (err) {
    console.warn('[stillsun] hull material upgrade unavailable', err);
  }
}

// =============================================================================================
// Regolith
// =============================================================================================

/** Warm glint colour: a grain of fused silica catching a 4.5° amber sun, not a white spark. */
const REGOLITH_GLINT_COLOR = 0xffd9a0;
/** Sparkle cells per metre. ~2 cm cells, about the size of a coarse grain that would glint. */
const REGOLITH_GLINT_CELLS_PER_M = 48;
/** Only cells whose hash clears this threshold glint at all — ~1.5% of the ground. */
const REGOLITH_GLINT_THRESHOLD = 0.985;
/** Specular exponent for a glint. Very tight: a grain either catches the sun or it does not. */
const REGOLITH_GLINT_POWER = 60;
/** How hard a glint is added over the albedo. Above the tone-mapping knee on purpose. */
const REGOLITH_GLINT_GAIN = 2.5;
/** View distance (m) over which sparkle fades out; past this it is pure sub-pixel aliasing. */
const REGOLITH_GLINT_NEAR = 22;
const REGOLITH_GLINT_FAR = 30;
/** Strength of the opposition surge (retro-reflection back toward the light). */
const REGOLITH_OPPOSITION = 0.18;
/** Falloff of the opposition surge — broad, it is a haze around the anti-solar point. */
const REGOLITH_OPPOSITION_POWER = 6;
/** Extra roughness on wind-scoured slopes, which expose coarser, less packed grain. */
const REGOLITH_SLOPE_ROUGHNESS = 0.05;
/** Grain relief amplitude in metres. 4 mm: the tooth you see under your own boots. */
const REGOLITH_GRAIN_BUMP_M = 0.004;
/** Grain frequency in cycles per metre: ~5 cm features. */
const REGOLITH_GRAIN_SCALE = 20;
/** View distance (m) over which the grain normal fades out. */
const REGOLITH_GRAIN_NEAR = 8;
const REGOLITH_GRAIN_FAR = 12;

/**
 * Adds the optical behaviour that makes sand read as sand, on top of the regolith material
 * `terrain.ts` already built (dune-scale colour, generated albedo blend, wind-ripple normal
 * with its own distance budget).
 *
 * Three of the four additions are things sand does that a plain rough dielectric does not:
 *
 * - **Sparkle.** Regolith on a fused world is full of specular grains. A sparse hash picks
 *   roughly 1.5% of 2 cm cells and gives each a very tight mirror lobe about the sun. At a
 *   4.5° elevation the mirror direction sweeps across the ground as the player walks, so the
 *   field twinkles from motion alone — no animation, no per-frame uniform. Note the glint is
 *   added to *albedo*, so it is still multiplied by N·L — and on flat ground under a 4.5° sun
 *   that is ~0.08, meaning most of what reaches the eye arrives via the hemisphere fill and
 *   the sky IBL. That is the intended (physically-shaped) behaviour, but if playtest calls it
 *   too dim the fix is to move the glint term to `emissiveNode`, where nothing gates it —
 *   *not* to raise `REGOLITH_GLINT_GAIN`, which would only blow out the few lit-face glints
 *   that already read.
 * - **Opposition effect.** Powdered regolith is retro-reflective: looking down-sun, shadows
 *   hide behind their own grains and the ground surges brighter. It is the single most
 *   recognisable "this is regolith, not concrete" cue, and it costs one dot and one pow.
 * - **Slope roughness.** Wind scours the packed fines off a steep face and leaves coarser
 *   grain, which is measurably rougher. Small, but it stops every dune face sharing one BRDF.
 * - **Grain micro-relief.** Near-field only, tiny amplitude, gone by 12 m.
 *
 * @param mat  The regolith material, mutated in place.
 * @param opts.tier    Low keeps only the opposition effect and the slope roughness — both
 *                     are pure ALU on terms already in the shader. Sparkle (a hash plus a
 *                     reflect per fragment over the entire terrain sheet) and the grain
 *                     normal (a noise plus two derivative pairs) are desktop-only.
 * @param opts.sunDir  World-space direction **to** the sun. Copied and normalised into a
 *                     uniform stored at `mat.userData.sunDirUniform`; a caller that moves the
 *                     sun (the Dimming, a future day cycle) writes `.value` on that uniform
 *                     with an already-normalised vector, and the shader is never rebuilt.
 */
export function upgradeRegolithMaterial(
  mat: MeshStandardNodeMaterial,
  opts: { tier: MaterialTier; sunDir: Vector3 },
): void {
  if (!renderFlags.noise) return;
  try {
    // Read each existing node exactly once. terrain.ts sets all three, but only inside its own
    // try/catch and only when `renderFlags.noise` held at build time, so none of them is
    // guaranteed and each needs the material's scalar defaults as a fallback.
    const baseColor: AnyNode = (mat.colorNode as AnyNode) ?? color(0x2e2622);
    const baseRoughness: AnyNode = (mat.roughnessNode as AnyNode) ?? float(0.9);
    const baseNormal: AnyNode = (mat.normalNode as AnyNode) ?? normalView;

    // Sun direction as a uniform, not a constant: a constant would bake into the shader and
    // any change to it would recompile every terrain material mid-raid. Normalised on the way
    // in (`computeSunDir()` already returns a unit vector, but nothing enforces that at this
    // boundary) — a caller writing `.value` later owes the same.
    const sunUniform = uniform(opts.sunDir.clone().normalize());

    // World-space view and light vectors, to pair with `normalWorld`. Deliberately *not*
    // `positionViewDirection`/`normalView`: mixing a view-space eye vector with a world-space
    // sun uniform would rotate the whole lighting response with the camera.
    const viewDir: AnyNode = cameraPosition.sub(positionWorld).normalize();
    const sunDirNode: AnyNode = sunUniform;
    const worldNormal: AnyNode = normalWorld;
    const viewDist: AnyNode = positionView.length();

    let surface: AnyNode = baseColor;

    // --- sparkle -----------------------------------------------------------------------
    if (opts.tier !== 'low') {
      // Piecewise-constant on purpose: `floor` quantises the ground into cells so a glint is
      // a discrete grain that pops on and off, not a smooth blob that slides around. This is
      // the one place in the file where a hash beats a noise.
      const cell = floor(positionWorld.xz.mul(REGOLITH_GLINT_CELLS_PER_M));
      const grain = hash2(cell);
      // `step` rather than a branch: the whole point is that this compiles to a select.
      const isGlint = step(REGOLITH_GLINT_THRESHOLD, grain);
      // A mirror lobe about the true reflection direction. `reflect` takes the *incident*
      // vector (pointing at the surface) in GLSL/TSL, hence the negated view direction.
      const mirror = reflect(viewDir.negate(), worldNormal);
      const spec = pow(max(dot(mirror, sunDirNode), 0), REGOLITH_GLINT_POWER);
      const glintFade = smoothstep(REGOLITH_GLINT_NEAR, REGOLITH_GLINT_FAR, viewDist).oneMinus();
      const glint = isGlint.mul(spec).mul(glintFade).mul(REGOLITH_GLINT_GAIN);
      surface = surface.add(color(REGOLITH_GLINT_COLOR).mul(glint));
    }

    // --- opposition effect -------------------------------------------------------------
    // Brightest where the eye looks straight down-sun (V·L -> 1), falling off over a broad
    // lobe. Applied to albedo rather than to the lighting model because it is a scattering
    // property of the powder, and this way it also lifts the ambient/IBL contribution, which
    // is where most of the shadowed ground's light comes from under a 4.5° key.
    const opposition = pow(max(dot(viewDir, sunDirNode), 0), REGOLITH_OPPOSITION_POWER);
    surface = surface.mul(float(1).add(opposition.mul(REGOLITH_OPPOSITION)));

    // --- slope roughness ---------------------------------------------------------------
    // 1 on a face steeper than the 0.85 normal-Y threshold, 0 on flat ground. Ascending edges
    // plus `oneMinus` for the same reason as the hull's grime mask: a descending `smoothstep`
    // is undefined behaviour per both shading languages, and the house idiom is this form.
    const steep = smoothstep(0.55, 0.85, worldNormal.y).oneMinus();
    const roughness: AnyNode = baseRoughness.add(steep.mul(REGOLITH_SLOPE_ROUGHNESS));

    // --- grain micro-relief ------------------------------------------------------------
    let normal: AnyNode = null;
    if (opts.tier !== 'low') {
      // `mx_noise_float`, not the sin-hash: this height is differentiated by `perturbNormal`,
      // and a hash is discontinuous — its screen-space derivative is zero inside a cell and
      // unbounded at every cell edge, which would draw a lattice of hard black seams across
      // the ground rather than grain. The noise is C1, its gradient is well-behaved, and it
      // is the same function terrain.ts's ripple normal uses one octave below, which is what
      // lets the two stack into one coherent surface instead of two competing ones.
      const grainHeight = mx_noise_float(positionWorld.xz.mul(REGOLITH_GRAIN_SCALE)).mul(
        REGOLITH_GRAIN_BUMP_M,
      );
      // Perturbs the *existing* normal (terrain.ts's already-rippled one), so the grain sits
      // on the ripples rather than replacing them.
      const perturbed = perturbNormal(baseNormal, grainHeight);
      const grainFade = smoothstep(REGOLITH_GRAIN_NEAR, REGOLITH_GRAIN_FAR, viewDist).oneMinus();
      normal = mix(baseNormal, perturbed, grainFade).normalize();
    }

    // All four writes together, after the last thing that can throw — including
    // `userData.sunDirUniform`, which would otherwise be left pointing at a uniform that no
    // shader reads, and which a caller would then update every frame to no effect.
    mat.userData.sunDirUniform = sunUniform;
    mat.colorNode = surface;
    mat.roughnessNode = roughness.clamp(0.05, 1);
    if (normal !== null) mat.normalNode = normal;
  } catch (err) {
    console.warn('[stillsun] regolith material upgrade unavailable', err);
  }
}

// =============================================================================================
// Lantern glass
// =============================================================================================

/** Unlit glass: smoked, sooty, nearly black. The emission does all the work. */
const LANTERN_GLASS_BASE = 0x1a120c;
/** Mean of the flicker envelope. */
const LANTERN_FLICKER_MEAN = 0.85;
/** Peak swing either side of the mean. */
const LANTERN_FLICKER_SWING = 0.15;
/** Hard floor on the envelope — a settlement lantern gutters, it never goes out. */
const LANTERN_FLICKER_FLOOR = 0.7;
/** Strength of the grazing-angle rim term. */
const LANTERN_RIM = 0.3;

/**
 * Emissive lantern glass — the amber accents that make the Spine read as inhabited.
 *
 * Two ideas beyond "set an emissive colour":
 *
 * - **A slow flicker.** Two incommensurate sines summed, so the envelope never visibly
 *   repeats, riding between `LANTERN_FLICKER_FLOOR` and 1. Driven by TSL's `time`, which is a
 *   uniform the renderer updates: the node graph is built once and the shader is never
 *   recompiled, unlike animating this from JS by reassigning `emissiveNode` each frame.
 * - **A rim.** Real glass has grazing-angle Fresnel: the edge of a curved or bevelled pane is
 *   always brighter than its face. Without it an emissive material is a flat coloured card
 *   whatever geometry it is on. The rim is tinted with the lantern's own colour rather than
 *   added as raw white, so a green or Hymn-white lantern keeps its identity at the edges.
 *
 * @param colorHex  Lantern colour (AD amber `0xffa040` for settlements, Hymn white
 *                  `0xdfe8ff` for the Choir Deck).
 * @param intensity Emissive multiplier. Values above 1 are intended — this is what the bloom
 *                  in `post.ts` picks up off hull edges.
 */
export function lanternGlassMaterial(
  colorHex: number,
  intensity: number,
): MeshStandardNodeMaterial {
  const mat = new MeshStandardNodeMaterial({
    color: LANTERN_GLASS_BASE,
    roughness: 0.25,
    metalness: 0,
  });
  // Set the non-node emissive *first*, so that if the TSL graph below throws (or is skipped)
  // the lantern is still a lit lantern rather than a black box — the emissiveNode assignment
  // simply overrides these when it succeeds.
  mat.emissive.setHex(colorHex);
  mat.emissiveIntensity = intensity;
  try {
    const tint = color(colorHex);

    // Two sines whose frequencies share no small common factor, so the sum's period is long
    // enough that the eye never locks onto it. The phase offset on the second keeps them from
    // starting aligned (which would make the first second of every raid flash in unison).
    const flicker = sin(time.mul(11.3))
      .mul(0.6)
      .add(sin(time.mul(4.7).add(1.7)).mul(0.4));
    const envelope = max(
      float(LANTERN_FLICKER_MEAN).add(flicker.mul(LANTERN_FLICKER_SWING)),
      float(LANTERN_FLICKER_FLOOR),
    );

    // Fresnel-ish rim in view space: `positionViewDirection` is the normalised fragment-to-eye
    // vector and pairs with `normalView`. (The regolith above uses world-space vectors instead
    // because it has to agree with a world-space sun uniform; here there is no light to agree
    // with, so view space is both correct and one transform cheaper.)
    const facing = max(dot(normalView, positionViewDirection), 0);
    const rim = pow(facing.oneMinus(), 3).mul(LANTERN_RIM);

    mat.emissiveNode = tint.mul(intensity).mul(envelope).add(tint.mul(rim));
  } catch (err) {
    console.warn('[stillsun] lantern glass emissive graph unavailable', err);
  }
  return mat;
}
